// src/HistoryPanel.js
import React, { useState, useLayoutEffect, useRef, useMemo, useEffect, useCallback } from 'react';
import './HistoryPanel.css';
import { formatBoardCoordinate } from './coordinateFormatting';

const PREVIEW_SUPPRESS_MS = 800;

const HistoryPanel = ({
    history,
    currentIndex,
    onHistoryClick,
    onPreviewHistoryEntry,
    viewMode,
    setViewMode,
    historyTree,
    onSelectTreeNode,
    onPreviewTreeEdge,
    onReplayStateChange,
    keyFocus,
    onRequestFocus,
    focusedHistoryIndex: externalHistoryIndex,
    registerToggleJumpEntry,
    onRequestBranch,
    suppressAutoStick = false,
    treePreviewActive = false,
    previewedTreeEdge = { parentId: null, childId: null },
    highlightToken = null,
    highlightHistoryIndex = null,
    highlightNodeId = null,
    branchReadyNodeId = null,
    coordSystem = 'endzone',
    boardRows = 0,
    boardCols = 0,
    viewPerspective = 1,
    onToggleVisualization = null,
    visualizationNodeId = null,
    nnVisualizationEnabled = false,
    nnDataStatus = new Map(),
    onGoLive = null,
    isInTurnMode = false,
    onExitTurnMode = null,
    hideLiveEntry = false,
}) => {
    const THUMB_SIZE = 16;
    const BOTTOM_TOLERANCE = 2;
    const [scrollTop, setScrollTop] = useState(0);
    const scrollTopRef = useRef(0);
    const [isDragging, setIsDragging] = useState(false);

    const [dimensions, setDimensions] = useState({
        containerHeight: 0,
        contentHeight: 0,
        maxScrollTop: 0,
        thumbHeight: 0,
    });

    const containerRef = useRef(null);
    const listRef = useRef(null);
    const scrollTrackRef = useRef(null);
    const prevHistoryLength = useRef(history.length);
    const [expandedMoves, setExpandedMoves] = useState(() => new Set());
    const [entryMetrics, setEntryMetrics] = useState([]);
    const [focusedHistoryIndex, setFocusedHistoryIndex] = useState(() => currentIndex ?? 0);
    const [hoveredHistoryIndex, setHoveredHistoryIndex] = useState(null);
    const highlightTimeoutRef = useRef(null);
    const [flashActive, setFlashActive] = useState(false);
    const autoScrollRef = useRef({ key: null, scrollTop: 0 });
    const alignmentGuardRef = useRef(false);
    const userScrollOverrideRef = useRef(false);
    const prevScrollTargetRef = useRef(null);
    const [treeScrollMetrics, setTreeScrollMetrics] = useState(() => ({
        scrollTop: 0,
        containerHeight: 0,
        contentHeight: 0,
        maxScrollTop: 0,
    }));
    const treeScrollControllerRef = useRef(null);

    const canFormatBoardCoords = boardRows > 0 && boardCols > 0;

    const formatCoordinatePair = useCallback((rowValue, colValue) => {
        if (!canFormatBoardCoords || !Number.isFinite(rowValue) || !Number.isFinite(colValue)) {
            const fallbackCol = Number.isFinite(colValue) ? colValue : '—';
            const fallbackRow = Number.isFinite(rowValue) ? rowValue : '—';
            return `(${fallbackRow}, ${fallbackCol})`;
        }
        const formatted = formatBoardCoordinate({
            position: [rowValue, colValue],
            numRows: boardRows,
            numCols: boardCols,
            coordSystem,
            viewPerspective,
        });
        return `(${formatted.rowLabel}, ${formatted.colLabel})`;
    }, [boardRows, boardCols, canFormatBoardCoords, coordSystem, viewPerspective]);

    const formatLooseCoordinate = useCallback((value) => {
        if (!value || typeof value !== 'string') {
            return value;
        }
        const match = value.match(/-?\d+/g);
        if (!match || match.length < 2) {
            return value;
        }
        const rowValue = Number(match[0]);
        const colValue = Number(match[1]);
        if (!Number.isFinite(colValue) || !Number.isFinite(rowValue)) {
            return value;
        }
        return formatCoordinatePair(rowValue, colValue);
    }, [formatCoordinatePair]);

    const formatInlineCoordinates = useCallback((text) => {
        if (typeof text !== 'string' || !text.includes('(')) {
            return text;
        }
        return text.replace(/\((-?\d+)\s*,\s*(-?\d+)\)/g, (_, row, col) => {
            const rowValue = Number(row);
            const colValue = Number(col);
            if (!Number.isFinite(colValue) || !Number.isFinite(rowValue)) {
                return `(${col}, ${row})`;
            }
            return formatCoordinatePair(rowValue, colValue);
        });
    }, [formatCoordinatePair]);

    const setScrollTopSafe = useCallback((updater) => {
        if (typeof updater === 'function') {
            setScrollTop(prev => {
                const next = updater(prev);
                if (typeof next !== 'number' || Number.isNaN(next)) {
                    return prev;
                }
                if (Math.abs(next - prev) < 0.5) {
                    scrollTopRef.current = prev;
                    return prev;
                }
                scrollTopRef.current = next;
                return next;
            });
        } else if (typeof updater === 'number' && !Number.isNaN(updater)) {
            if (Math.abs(updater - scrollTopRef.current) < 0.5) {
                return;
            }
            scrollTopRef.current = updater;
            setScrollTop(updater);
        }
    }, []);

    const handleTreeScrollMetricsChange = useCallback((metrics) => {
        // Skip state churn when metrics are byte-identical. Without this,
        // the useLayoutEffect that calls reportTreeScrollMetrics fires a
        // fresh object every commit; if anything upstream re-renders with
        // a new historyTree identity (which OnlineGame's preview path
        // does), the layout effect refires forever and React aborts with
        // error #185.
        setTreeScrollMetrics(prev => {
            if (!metrics) {
                if (prev.scrollTop === 0 && prev.containerHeight === 0
                    && prev.contentHeight === 0 && prev.maxScrollTop === 0) {
                    return prev;
                }
                return { scrollTop: 0, containerHeight: 0, contentHeight: 0, maxScrollTop: 0 };
            }
            if (prev.scrollTop === metrics.scrollTop
                && prev.containerHeight === metrics.containerHeight
                && prev.contentHeight === metrics.contentHeight
                && prev.maxScrollTop === metrics.maxScrollTop) {
                return prev;
            }
            return metrics;
        });
    }, []);

    const registerTreeScrollController = useCallback((controller) => {
        treeScrollControllerRef.current = controller;
    }, []);

    useEffect(() => {
        if (!highlightToken) {
            return undefined;
        }
        if (highlightTimeoutRef.current) {
            window.clearTimeout(highlightTimeoutRef.current);
        }
        setFlashActive(true);
        highlightTimeoutRef.current = window.setTimeout(() => {
            setFlashActive(false);
            highlightTimeoutRef.current = null;
        }, 900);
        return () => {
            if (highlightTimeoutRef.current) {
                window.clearTimeout(highlightTimeoutRef.current);
                highlightTimeoutRef.current = null;
            }
        };
    }, [highlightToken]);

    const resolvedHistoryTree = historyTree ?? { root: null, currentNodeId: null, liveNodeId: null };
    const historyLength = history.length;
    const handleSelectTreeNode = onSelectTreeNode ?? (() => {});
    const handlePreviewTreeEdge = onPreviewTreeEdge ?? (() => {});

    const nodesById = useMemo(() => {
        const map = new Map();
        const traverse = (node) => {
            if (!node || !node.id) {
                return;
            }
            map.set(node.id, node);
            (node.children ?? []).forEach(traverse);
        };
        if (resolvedHistoryTree?.root) {
            traverse(resolvedHistoryTree.root);
        }
        return map;
    }, [resolvedHistoryTree?.root]);

    const liveNodeId = resolvedHistoryTree?.liveNodeId ?? null;
    const currentNodeId = resolvedHistoryTree?.currentNodeId ?? null;
    const liveNode = liveNodeId && nodesById.has(liveNodeId) ? nodesById.get(liveNodeId) : null;
    const livePlayer = typeof liveNode?.statePlayer === 'number' ? liveNode.statePlayer : null;
    const livePlayerLabel = livePlayer != null ? `Player ${livePlayer}` : 'Live position';
    const liveDescription = `${livePlayerLabel} · Resume play`;
    const atLiveNode = Boolean(currentNodeId && liveNodeId && currentNodeId === liveNodeId);
    const liveEntryActive = atLiveNode || (typeof currentIndex === 'number' && currentIndex >= historyLength);

    const ARROW = '→';

    const parsedHistory = useMemo(() => {
        return history.map((entry) => {
            if (typeof entry !== 'string') {
                const summary = String(entry ?? '');
                return {
                    type: 'info',
                    summary: formatInlineCoordinates(summary),
                    fullText: formatInlineCoordinates(summary),
                    segments: [],
                    isMulti: false,
                };
            }

            const jumpSplit = entry.split(': Jump');
            if (jumpSplit.length < 2) {
                const formatted = formatInlineCoordinates(entry);
                return { type: 'placement', summary: formatted, fullText: formatted, segments: [], isMulti: false };
            }

            const header = `${jumpSplit[0]}: Jump`;
            const rest = jumpSplit.slice(1).join(': Jump').trim();
            const segments = rest.split('->').map(seg => seg.replace(/\s+/g, ' ').trim()).filter(Boolean);
            const isMulti = segments.length > 2;
            const joinSegments = (values) => values.join(` ${ARROW} `);
            const summary = isMulti
                ? `${jumpSplit[0]}: Jump ${segments[0]} ${ARROW} … ${ARROW} ${segments[segments.length - 1]}`
                : `${jumpSplit[0]}: Jump ${joinSegments(segments)}`;

            return {
                type: 'jump',
                summary: formatInlineCoordinates(summary),
                fullText: formatInlineCoordinates(`${jumpSplit[0]}: Jump ${joinSegments(segments)}`.trim()),
                segments: segments.map(seg => formatLooseCoordinate(seg)),
                header: formatInlineCoordinates(header),
                isMulti,
            };
        });
    }, [formatInlineCoordinates, formatLooseCoordinate, history]);

    const clampHistoryIndex = (value, { includeLive = false } = {}) => {
        const entryCount = parsedHistory.length;
        if (!entryCount) {
            return 0;
        }
        const upperBound = includeLive ? entryCount : Math.max(entryCount - 1, 0);
        const fallback = (() => {
            if (typeof currentIndex === 'number' && !Number.isNaN(currentIndex)) {
                return Math.max(0, Math.min(currentIndex, upperBound));
            }
            return 0;
        })();
        if (typeof value !== 'number' || Number.isNaN(value)) {
            return fallback;
        }
        return Math.max(0, Math.min(value, upperBound));
    };

    const hasHistoryFocus = keyFocus === 'history';
    const resolvedFocusedIndex = clampHistoryIndex(focusedHistoryIndex, { includeLive: true });
    const resolvedCurrentIndex = clampHistoryIndex(currentIndex);

    const focusIndex = clampHistoryIndex(resolvedFocusedIndex);
    const selectionIndex = resolvedCurrentIndex;

    const liveEntryFocused = hasHistoryFocus && resolvedFocusedIndex === parsedHistory.length;
    const highlightIndex = hasHistoryFocus ? focusIndex : selectionIndex;
    const shouldFollowLive = liveEntryFocused || (!hasHistoryFocus && liveEntryActive);
    const scrollTargetIndex = shouldFollowLive ? parsedHistory.length : highlightIndex;

    const shouldFlashLiveEntry = flashActive
        && typeof highlightHistoryIndex === 'number'
        && highlightHistoryIndex >= parsedHistory.length;

    useLayoutEffect(() => {
        setExpandedMoves(prev => {
            const next = new Set();
            parsedHistory.forEach((entry, index) => {
                if (entry.type === 'jump' && entry.isMulti && prev.has(index)) {
                    next.add(index);
                }
            });
            return next;
        });
    }, [parsedHistory]);

    const stickToBottomRef = useRef(true);

    useEffect(() => {
        scrollTopRef.current = scrollTop;
    }, [scrollTop]);

    const computeAlignedScrollTop = (value, metrics, containerHeight, maxScroll) => {
        const clamp = (v) => Math.max(0, Math.min(v, maxScroll));
        if (!metrics || metrics.length === 0 || containerHeight <= 0) {
            return clamp(value);
        }

        let target = clamp(value);
        let attempts = 0;
        while (attempts < 4) {
            attempts += 1;
            let firstIdx = metrics.findIndex(metric => metric.bottom > target + 0.5);
            if (firstIdx === -1) firstIdx = metrics.length - 1;
            const firstMetric = metrics[firstIdx];
            if (Math.abs(firstMetric.top - target) > 0.5) {
                target = clamp(firstMetric.top);
                continue;
            }

            const viewportBottom = target + containerHeight;
            let overflowIdx = -1;
            for (let i = firstIdx; i < metrics.length; i += 1) {
                if (metrics[i].bottom > viewportBottom + 0.5) {
                    overflowIdx = i;
                    break;
                }
            }

            if (overflowIdx === -1) {
                return clamp(target);
            }

            const overflowMetric = metrics[overflowIdx];
            const newTarget = clamp(overflowMetric.bottom - containerHeight);

            if (Math.abs(newTarget - target) < 0.5) {
                const firstIndex = metrics.findIndex(metric => metric.bottom > newTarget + 0.5);
                if (firstIndex !== -1) {
                    return clamp(metrics[firstIndex].top);
                }
                return clamp(newTarget);
            }

            target = newTarget;
        }

        return clamp(target);
    };

    const scrollToBottomRef = useRef(false);

    useEffect(() => {
        if (suppressAutoStick) {
            stickToBottomRef.current = false;
        }
    }, [suppressAutoStick]);

    useEffect(() => {
        scrollToBottomRef.current = parsedHistory.length > prevHistoryLength.current && currentIndex === history.length - 1;
    }, [parsedHistory.length, currentIndex, history.length]);

    const handleHistorySelect = useCallback((index) => {
        let numericIndex = null;
        if (typeof index === 'number' && Number.isFinite(index)) {
            numericIndex = index;
        } else {
            const coerced = Number(index);
            if (Number.isFinite(coerced)) {
                numericIndex = coerced;
            }
        }

        if (numericIndex == null) {
            return;
        }

        const entryCount = parsedHistory.length;
        const lastIndex = entryCount > 0 ? entryCount - 1 : 0;
        const isLiveSelection = numericIndex >= entryCount;
        const targetIndex = isLiveSelection
            ? entryCount
            : Math.max(0, Math.min(numericIndex, lastIndex));
        setFocusedHistoryIndex(targetIndex);
        onRequestFocus?.('history');
        onHistoryClick(targetIndex);
        userScrollOverrideRef.current = false;
    }, [onHistoryClick, onRequestFocus, parsedHistory.length]);

    const handleHistoryHover = useCallback((index, active) => {
        if (typeof onPreviewHistoryEntry !== 'function') {
            setHoveredHistoryIndex(active ? index : null);
            return;
        }
        if (!active) {
            setHoveredHistoryIndex(null);
            onPreviewHistoryEntry(null, false);
            return;
        }
        const entryCount = parsedHistory.length;
        if (!entryCount) {
            return;
        }
        if (typeof index === 'number' && index >= entryCount) {
            setHoveredHistoryIndex(entryCount);
            onPreviewHistoryEntry(entryCount, true);
            return;
        }
        const boundedIndex = Math.max(0, Math.min(typeof index === 'number' ? index : 0, entryCount - 1));
        setHoveredHistoryIndex(boundedIndex);
        onPreviewHistoryEntry(boundedIndex, true);
    }, [onPreviewHistoryEntry, parsedHistory.length]);

    const toggleJumpEntry = useCallback((index) => {
        if (viewMode !== 'linear') return false;
        if (!Array.isArray(parsedHistory) || parsedHistory.length === 0) return false;
        const bounded = Math.max(0, Math.min(typeof index === 'number' ? index : focusedHistoryIndex, parsedHistory.length - 1));
        const entry = parsedHistory[bounded];
        const canToggle = entry?.type === 'jump' && entry.isMulti;
        if (!canToggle) {
            return false;
        }

        const wasExpanded = expandedMoves.has(bounded);
        
        setExpandedMoves(prev => {
            const next = new Set(prev);
            if (next.has(bounded)) {
                next.delete(bounded);
            } else {
                next.add(bounded);
            }
            return next;
        });

        // When expanding (not collapsing), scroll to bottom if entry is in lower portion
        // This ensures the expanded content is visible
        // Use setTimeout to allow DOM to update first
        if (!wasExpanded && bounded >= Math.floor(parsedHistory.length / 2)) {
            setTimeout(() => {
                scrollToBottomRef.current = true;
                // Trigger a re-render to process the scroll
                setScrollTop(prev => prev);
            }, 50);
        }

        return true;
    }, [focusedHistoryIndex, parsedHistory, viewMode, expandedMoves]);

    useEffect(() => {
        if (typeof registerToggleJumpEntry === 'function') {
            registerToggleJumpEntry(toggleJumpEntry);
            return () => registerToggleJumpEntry(null);
        }
        return undefined;
    }, [registerToggleJumpEntry, toggleJumpEntry]);

    useEffect(() => {
        if (viewMode !== 'linear') return;
        if (keyFocus !== 'history') return;
        if (typeof externalHistoryIndex === 'number') {
            setFocusedHistoryIndex(Math.max(0, Math.min(externalHistoryIndex, parsedHistory.length)));
        } else {
            const fallback = typeof currentIndex === 'number' ? currentIndex : 0;
            const clamped = parsedHistory.length > 0
                ? Math.max(0, Math.min(fallback, parsedHistory.length - 1))
                : 0;
            setFocusedHistoryIndex(clamped);
        }
    }, [viewMode, currentIndex, parsedHistory.length, keyFocus, externalHistoryIndex]);


    useLayoutEffect(() => {
        if (!containerRef.current || !listRef.current) {
            return;
        }

        const listRect = listRef.current.getBoundingClientRect();
        const measuredViewport = listRect.height; // actual visible height (excludes panel padding)
        const newContainerHeight = measuredViewport > 0
            ? measuredViewport
            : containerRef.current.clientHeight;
        const children = Array.from(listRef.current.children);
        const metrics = [];
        let runningTop = 0;

        children.forEach((child) => {
            const height = child.getBoundingClientRect().height;
            const top = runningTop;
            const bottom = top + height;
            metrics.push({ top, height, bottom });
            runningTop = bottom;
        });

        const newContentHeight = runningTop;
        const newMaxScrollTop = Math.max(0, newContentHeight - newContainerHeight);
        const newThumbHeight = THUMB_SIZE;

        setEntryMetrics(metrics);
        setDimensions(prev => {
            if (
                prev.containerHeight === newContainerHeight &&
                prev.contentHeight === newContentHeight &&
                Math.abs(prev.maxScrollTop - newMaxScrollTop) < 0.5 &&
                prev.thumbHeight === newThumbHeight
            ) {
                return prev;
            }
            return {
                containerHeight: newContainerHeight,
                contentHeight: newContentHeight,
                maxScrollTop: newMaxScrollTop,
                thumbHeight: newThumbHeight,
            };
        });

        const historyGrew = history.length !== prevHistoryLength.current;
        const shouldStickToBottom = !suppressAutoStick && (historyGrew || scrollToBottomRef.current);

        if (suppressAutoStick) {
            prevHistoryLength.current = history.length;
            scrollToBottomRef.current = false;
            stickToBottomRef.current = false;
            return;
        }

        if (shouldStickToBottom) {
            prevHistoryLength.current = history.length;
            scrollToBottomRef.current = false;
            stickToBottomRef.current = true;
            if (!alignmentGuardRef.current && Math.abs(newMaxScrollTop - scrollTop) > 0.5) {
                const target = newMaxScrollTop;
                setScrollTopSafe(target);
                requestAnimationFrame(() => {
                    setScrollTopSafe(target);
                });
            }
            alignmentGuardRef.current = false;
            return;
        }

        const alignedScroll = computeAlignedScrollTop(scrollTop, metrics, newContainerHeight, newMaxScrollTop);

        prevHistoryLength.current = history.length;
        scrollToBottomRef.current = false;

        if (!alignmentGuardRef.current && Math.abs(alignedScroll - scrollTop) > 0.5) {
            setScrollTopSafe(alignedScroll);
        }
        alignmentGuardRef.current = false;
        stickToBottomRef.current = Math.abs(alignedScroll - newMaxScrollTop) < BOTTOM_TOLERANCE;
    }, [history.length, viewMode, parsedHistory, expandedMoves, scrollTop, suppressAutoStick]);

    const { containerHeight, contentHeight, maxScrollTop, thumbHeight } = dimensions;
    const isTreeView = viewMode === 'tree';
    const activeScrollTop = isTreeView ? treeScrollMetrics.scrollTop : scrollTop;
    const activeMaxScrollTop = isTreeView ? treeScrollMetrics.maxScrollTop : maxScrollTop;
    const activeThumbHeight = isTreeView ? THUMB_SIZE : thumbHeight;
    const activeContainerHeight = isTreeView ? treeScrollMetrics.containerHeight : containerHeight;
    const activeContentHeight = isTreeView ? treeScrollMetrics.contentHeight : contentHeight;

    useEffect(() => {
        if (history.length === 0) return;
        if (currentIndex < history.length - 1) return;
        if (suppressAutoStick || !stickToBottomRef.current) return;
        if (Math.abs(scrollTop - maxScrollTop) < BOTTOM_TOLERANCE) return;
        setScrollTopSafe(maxScrollTop);
    }, [history.length, currentIndex, maxScrollTop, scrollTop, suppressAutoStick]);

    const trackHeight = scrollTrackRef.current?.clientHeight ?? Math.max(activeContainerHeight, 0);
    const thumbRange = Math.max(trackHeight - activeThumbHeight, 0);
    const thumbPosition = (activeMaxScrollTop > 0 && thumbRange > 0)
        ? (activeScrollTop / activeMaxScrollTop) * thumbRange
        : 0;

    const handleWheelScroll = (e) => {
        if (viewMode !== 'linear') return;
        if (maxScrollTop <= 0) return;
        e.preventDefault();
        stickToBottomRef.current = false;
        userScrollOverrideRef.current = true;
        setScrollTopSafe(prev => {
            const next = computeAlignedScrollTop(prev + e.deltaY, entryMetrics, containerHeight, maxScrollTop);
            const clamped = Math.max(0, Math.min(maxScrollTop, next));
            stickToBottomRef.current = !suppressAutoStick && Math.abs(clamped - maxScrollTop) < BOTTOM_TOLERANCE;
            return clamped;
        });
    };

    const handlePointerDown = (e) => {
        const trackEl = scrollTrackRef.current;
        if (!trackEl) return;
        if (activeMaxScrollTop <= 0) return;
        if (isTreeView && !treeScrollControllerRef.current) return;

        e.preventDefault();
        setIsDragging(true);
        const pid = e.pointerId;
        e.target.setPointerCapture(pid);

        if (!isTreeView) {
            userScrollOverrideRef.current = true;
        }

        const dragMode = viewMode;
        const startY = e.clientY;
        const startScrollTop = dragMode === 'tree' ? treeScrollMetrics.scrollTop : scrollTop;
        const thumbSize = activeThumbHeight;
        const scrollableDist = trackEl.clientHeight - thumbSize;
        const maxScrollable = dragMode === 'tree' ? treeScrollMetrics.maxScrollTop : maxScrollTop;

        if (scrollableDist <= 0 || maxScrollable <= 0) {
            setIsDragging(false);
            return;
        }

        const scrollRatio = maxScrollable / scrollableDist;
        const treeController = treeScrollControllerRef.current;

        const handlePointerMove = (moveEvent) => {
            const deltaY = moveEvent.clientY - startY;
            const next = Math.max(0, Math.min(maxScrollable, startScrollTop + (deltaY * scrollRatio)));

            if (dragMode === 'tree') {
                if (treeController && typeof treeController.scrollTo === 'function') {
                    treeController.scrollTo(next);
                }
            } else {
                stickToBottomRef.current = !suppressAutoStick && Math.abs(next - maxScrollable) < BOTTOM_TOLERANCE;
                setScrollTopSafe(next);
            }
        };

        const handlePointerUp = () => {
            setIsDragging(false);
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
            window.removeEventListener('pointercancel', handlePointerUp);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        window.addEventListener('pointercancel', handlePointerUp);
    };

    const handleTrackClick = (e) => {
        // Ignore if clicking on the thumb itself
        if (e.target.classList.contains('panel-scroll-thumb')) return;

        const trackEl = scrollTrackRef.current;
        if (!trackEl) return;
        if (activeMaxScrollTop <= 0) return;

        const trackRect = trackEl.getBoundingClientRect();
        const clickY = e.clientY - trackRect.top;
        const trackHeight = trackRect.height;
        const scrollRatio = clickY / trackHeight;
        const targetScroll = Math.max(0, Math.min(activeMaxScrollTop, scrollRatio * activeMaxScrollTop));

        if (isTreeView) {
            if (treeScrollControllerRef.current?.scrollTo) {
                treeScrollControllerRef.current.scrollTo(targetScroll);
            }
        } else {
            stickToBottomRef.current = !suppressAutoStick && Math.abs(targetScroll - maxScrollTop) < BOTTOM_TOLERANCE;
            userScrollOverrideRef.current = true;
            setScrollTopSafe(targetScroll);
        }
    };

    useEffect(() => {
        if (viewMode !== 'linear') return;
        if (isDragging) return;
        if (!entryMetrics.length || containerHeight <= 0) return;
        if (scrollTargetIndex < 0 || scrollTargetIndex >= entryMetrics.length) return;
        const targetMetric = entryMetrics[scrollTargetIndex];
        if (!targetMetric) return;

        if (prevScrollTargetRef.current !== scrollTargetIndex) {
            prevScrollTargetRef.current = scrollTargetIndex;
            userScrollOverrideRef.current = false;
        }

        if (userScrollOverrideRef.current) {
            return;
        }

        const margin = 12;
        const viewportTop = scrollTopRef.current;
        const viewportBottom = viewportTop + containerHeight;
        const roundedTop = Math.round(targetMetric.top);
        const roundedBottom = Math.round(targetMetric.bottom);
        const key = `${scrollTargetIndex}:${roundedTop}:${roundedBottom}`;
        const lastAuto = autoScrollRef.current;

        const fullyVisible = (targetMetric.top >= viewportTop + margin)
            && (targetMetric.bottom <= viewportBottom - margin);
        const wantsAutoScroll = (!fullyVisible && Math.abs(scrollTopRef.current - lastAuto.scrollTop) > 2)
            || (lastAuto.key !== key);

        if (!wantsAutoScroll && !stickToBottomRef.current) {
            return;
        }

        if (!wantsAutoScroll) {
            autoScrollRef.current = { key, scrollTop: scrollTopRef.current };
            return;
        }

        let nextScroll = scrollTopRef.current;

        if (targetMetric.top < viewportTop + margin) {
            nextScroll = Math.max(0, targetMetric.top - margin);
        } else if (targetMetric.bottom > viewportBottom - margin) {
            nextScroll = Math.min(maxScrollTop, Math.max(0, targetMetric.bottom - containerHeight + margin));
        }

        if (Math.abs(nextScroll - scrollTopRef.current) > 0.5) {
            alignmentGuardRef.current = true;
            setScrollTopSafe(nextScroll);
            autoScrollRef.current = { key, scrollTop: nextScroll };
        }
    }, [scrollTargetIndex, entryMetrics, containerHeight, viewMode, isDragging, parsedHistory.length, maxScrollTop]);

    const handleViewModeChange = useCallback((mode, event) => {
        setViewMode(mode);
        if (event && typeof event.currentTarget?.blur === 'function') {
            event.currentTarget.blur();
        }
    }, [setViewMode]);

    return (
        <div className="history-panel">
            <div className="tabs">
                <button
                    className={`tab-button ${viewMode === 'linear' ? 'active' : ''}`}
                    onClick={(event) => handleViewModeChange('linear', event)}
                    title="Linear View"
                >
                    ↓
                </button>
                <button
                    className={`tab-button ${viewMode === 'tree' ? 'active' : ''}`}
                    onClick={(event) => handleViewModeChange('tree', event)}
                    title="Tree View"
                >
                    ⋔
                </button>
            </div>

            <div
                ref={containerRef}
                className="history-content-wrapper"
                onWheel={handleWheelScroll}
            >
                {viewMode === 'linear' ? (
                    <ul
                        ref={listRef}
                        className="history-list"
                        style={{ transform: `translateY(-${scrollTop}px)` }}
                    >
                        {parsedHistory.map((entry, index) => {
                            const showSelectionHighlight = !liveEntryFocused;
                            const isSelected = !liveEntryActive && index === selectionIndex;
                            const effectiveSelected = showSelectionHighlight && isSelected;
                            const isFocused = keyFocus === 'history' && resolvedFocusedIndex === index && !effectiveSelected;
                            const isExpanded = expandedMoves.has(index);
                            const isJump = entry.type === 'jump';
                            const showToggle = isJump && entry.isMulti;
                            const isHovered = hoveredHistoryIndex === index;

                            const itemClassNamesArr = [
                                'history-item',
                                isJump ? 'jump-entry' : 'placement-entry',
                                effectiveSelected ? 'active' : '',
                                isFocused ? 'focused' : '',
                                isHovered && !effectiveSelected ? 'hovered' : '',
                                isExpanded ? 'expanded' : '',
                            ];
                            if (flashActive && highlightHistoryIndex === index) {
                                itemClassNamesArr.push('highlight-flash');
                            }
                            const itemClassNames = itemClassNamesArr.filter(Boolean).join(' ');

                            const handleToggle = (event) => {
                                event.stopPropagation();
                                toggleJumpEntry(index);
                                event.currentTarget.blur();
                            };

                            return (
                                <li
                                    key={index}
                                    className={itemClassNames}
                                    onClick={() => handleHistorySelect(index)}
                                    onMouseEnter={() => handleHistoryHover(index, true)}
                                    onMouseLeave={() => handleHistoryHover(index, false)}
                                >
                                    <div className="history-entry-line">
                                        {showToggle && (
                                            <button
                                                type="button"
                                                className="expand-toggle"
                                                onClick={handleToggle}
                                                aria-label={isExpanded ? 'Collapse jump path' : 'Expand jump path'}
                                            >
                                                {isExpanded ? '▾' : '▸'}
                                            </button>
                                        )}
                                        <span className="entry-text">
                                            {isJump
                                                ? (entry.isMulti
                                                    ? (isExpanded ? entry.header : entry.summary)
                                                    : entry.fullText)
                                                : entry.summary}
                                        </span>
                                    </div>
                                    {isJump && isExpanded && (
                                        <div className="jump-expanded-line">
                                            {entry.segments.map((segment, segIdx) => (
                                                <span key={segIdx} className="jump-segment">
                                                    {segIdx > 0 && <span className="jump-arrow">→</span>}
                                                    <span className="jump-segment-text">{segment}</span>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                        {!hideLiveEntry && (
                        <li
                            key="live-entry"
                            className={[
                                'history-item',
                                'live-entry',
                                (liveEntryActive && !(hasHistoryFocus && !liveEntryFocused)) ? 'active' : '',
                                (!liveEntryActive && !liveEntryFocused) ? 'pending' : '',
                                liveEntryFocused ? 'focused' : '',
                                (!liveEntryActive && hoveredHistoryIndex === parsedHistory.length) ? 'hovered' : '',
                                shouldFlashLiveEntry ? 'highlight-flash' : '',
                            ].filter(Boolean).join(' ')}
                            onClick={() => handleHistorySelect(parsedHistory.length)}
                            onMouseEnter={() => handleHistoryHover(parsedHistory.length, true)}
                            onMouseLeave={() => handleHistoryHover(parsedHistory.length, false)}
                        >
                            <div className="history-entry-line">
                                <span className="entry-badge live">Live</span>
                                <span className="entry-text">
                                    {liveDescription}
                                </span>
                            </div>
                        </li>
                        )}
                    </ul>
                ) : (
                    <TreeHistory
                        historyTree={resolvedHistoryTree}
                        currentNodeId={resolvedHistoryTree.currentNodeId}
                        onSelectNode={handleSelectTreeNode}
                        onPreviewTreeEdge={handlePreviewTreeEdge}
                        onReplayStateChange={onReplayStateChange}
                        keyFocus={keyFocus}
                        onRequestFocus={onRequestFocus}
                        isPreviewing={treePreviewActive}
                        previewedTreeEdge={previewedTreeEdge}
                        onRequestBranch={onRequestBranch}
                        flashActive={flashActive}
                        highlightNodeId={highlightNodeId}
                        branchReadyNodeId={branchReadyNodeId}
                        onScrollMetricsChange={handleTreeScrollMetricsChange}
                        registerScrollController={registerTreeScrollController}
                        formatCoordinate={formatLooseCoordinate}
                        onToggleVisualization={onToggleVisualization}
                        visualizationNodeId={visualizationNodeId}
                        nnVisualizationEnabled={nnVisualizationEnabled}
                        nnDataStatus={nnDataStatus}
                        onGoLive={onGoLive}
                        isInTurnMode={isInTurnMode}
                        onExitTurnMode={onExitTurnMode}
                    />
                )}
                
                {(viewMode === 'linear' || viewMode === 'tree') && (
                    <div ref={scrollTrackRef} className="panel-scroll-track panel-scroll-track--right" onClick={handleTrackClick}>
                        <div
                            className={`panel-scroll-thumb ${isDragging ? 'dragging' : ''} ${activeMaxScrollTop <= 0 ? 'disabled' : ''}`}
                            style={{
                                height: `${Math.max(activeThumbHeight, THUMB_SIZE)}px`,
                                top: `${thumbPosition}px`,
                            }}
                            onPointerDown={handlePointerDown}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default HistoryPanel;

const parseTreeMoveSummary = (summary) => {
    if (!summary || summary === 'start') {
        return {
            player: null,
            action: 'START',
            coordinates: [],
        };
    }

    const firstDash = summary.indexOf('-');
    if (firstDash === -1) {
        return {
            player: null,
            action: summary.toUpperCase(),
            coordinates: [],
        };
    }

    const player = summary.slice(0, firstDash);
    const remainder = summary.slice(firstDash + 1);
    const secondDash = remainder.indexOf('-');

    let actionPart = remainder;
    let rest = '';
    if (secondDash !== -1) {
        actionPart = remainder.slice(0, secondDash);
        rest = remainder.slice(secondDash + 1);
    }

    const action = (actionPart || 'MOVE').toUpperCase();
    const coordinates = [];
    const matches = rest.match(/\([^)]*\)/g);

    if (matches) {
        matches.forEach(match => {
            const cleaned = match.replace(/\s+/g, ' ').trim();
            if (cleaned) coordinates.push(cleaned);
        });
    }

    return {
        player: player || null,
        action,
        coordinates,
    };
};

const normalizeCoordinate = (coord) => {
    if (!coord) return '';
    return coord
        .replace(/\s*,\s*/g, ', ')
        .replace(/\s+/g, ' ')
        .trim();
};

const buildTreeMoveDisplay = (node) => {
    const info = parseTreeMoveSummary(node?.moveSummary);
    let action = info.action;
    if (node?.isHalt) {
        action = 'HALT';
    } else if (node?.isJump) {
        action = 'JUMP';
    } else if (typeof action === 'string') {
        if (action.toUpperCase().includes('HALT')) {
            action = 'HALT';
        } else if (action.toUpperCase().includes('JUMP')) {
            action = 'JUMP';
        } else if (action.toUpperCase().includes('PLACE')) {
            action = 'PLACE';
        }
    }

    const normalized = info.coordinates.map(normalizeCoordinate);
    const startCoord = normalized[0] ?? null;
    const endCoord = normalized.length >= 2 ? normalized[normalized.length - 1] : null;

    return {
        isStart: action === 'START',
        playerLabel: info.player ?? (node?.player ? `P${node.player}` : 'P?'),
        actionLabel: action === 'START' ? null : action,
        startCoord,
        endCoord,
        singleCoord: (!endCoord && startCoord) ? startCoord : null,
    };
};

const normalizePreviewEdge = (edge) => {
    if (edge && edge.parentId && edge.childId) {
        return { parentId: edge.parentId, childId: edge.childId };
    }
    return { parentId: null, childId: null };
};

const previewEdgesEqual = (a, b) => {
    const aParent = a?.parentId ?? null;
    const aChild = a?.childId ?? null;
    const bParent = b?.parentId ?? null;
    const bChild = b?.childId ?? null;
    return aParent === bParent && aChild === bChild;
};

const TreeHistory = ({
    historyTree,
    currentNodeId,
    onSelectNode,
    onPreviewTreeEdge,
    onReplayStateChange,
    keyFocus,
    onRequestFocus,
    isPreviewing = false,
    previewedTreeEdge = { parentId: null, childId: null },
    onRequestBranch,
    flashActive = false,
    highlightNodeId = null,
    branchReadyNodeId = null,
    onScrollMetricsChange,
    registerScrollController,
    formatCoordinate = (value) => value,
    onToggleVisualization = null,
    visualizationNodeId = null,
    nnVisualizationEnabled = false,
    nnDataStatus = new Map(),
    onGoLive = null,
    isInTurnMode = false, // true when viewing from next player's perspective (ready to play)
    onExitTurnMode = null, // callback to exit turn mode and view HALT move
}) => {
    const [openBranchFor, setOpenBranchFor] = useState(null);
    const [focusedBranchIndex, setFocusedBranchIndex] = useState(0);
    const [isReplaying, setIsReplaying] = useState(false);
    const [replaySpeed, setReplaySpeed] = useState(1); // 0: 1/2x, 1: 1x (default), 2: 2x
    const [speedFlash, setSpeedFlash] = useState(false);
    const replaySpeedRef = useRef(1);
    const treeContainerRef = useRef(null);
    const prevPathLengthRef = useRef(0);
    const prevSelectedNodeRef = useRef(null);
    const firstRenderRef = useRef(true);
    const branchMenuRef = useRef(null);
    const turnIndicatorRef = useRef(null);
    const skipPreviewClearRef = useRef(false);
    const replayStateRef = useRef({ active: false, queue: [], timeoutId: null });
    const selectNodeRef = useRef(null); // Ref to always call latest selectNode during replay
    const nodeElementRefs = useRef(new Map());
    const lastPreviewActivityRef = useRef(0);
    const prevPreviewingRef = useRef(isPreviewing);
    const [hoveredNodeId, setHoveredNodeId] = useState(null);
    const formatDisplay = useCallback((node) => {
        const base = buildTreeMoveDisplay(node);
        return {
            ...base,
            startCoord: formatCoordinate(base.startCoord),
            endCoord: formatCoordinate(base.endCoord),
            singleCoord: formatCoordinate(base.singleCoord),
        };
    }, [formatCoordinate]);
    const reportTreeScrollMetrics = useCallback(() => {
        if (typeof onScrollMetricsChange !== 'function') {
            return;
        }
        const container = treeContainerRef.current;
        if (!container) {
            return;
        }
        const { scrollTop, scrollHeight, clientHeight } = container;
        onScrollMetricsChange({
            scrollTop,
            containerHeight: clientHeight,
            contentHeight: scrollHeight,
            maxScrollTop: Math.max(0, scrollHeight - clientHeight),
        });
    }, [onScrollMetricsChange]);

    useEffect(() => {
        const now = Date.now();
        if (isPreviewing) {
            lastPreviewActivityRef.current = now;
        } else if (prevPreviewingRef.current) {
            lastPreviewActivityRef.current = now;
        }
        prevPreviewingRef.current = isPreviewing;
    }, [isPreviewing]);

    useEffect(() => {
        if (typeof registerScrollController !== 'function') {
            return undefined;
        }
        const controller = {
            scrollTo: (top) => {
                if (!treeContainerRef.current) return;
                treeContainerRef.current.scrollTop = top;
            },
        };
        registerScrollController(controller);
        return () => registerScrollController(null);
    }, [registerScrollController]);

    const previewRecentlyActive = () => (Date.now() - lastPreviewActivityRef.current) < PREVIEW_SUPPRESS_MS;

    const nodesById = useMemo(() => {
        const map = new Map();
        const traverse = (node) => {
            if (!node) return;
            map.set(node.id, node);
            (node.children ?? []).forEach(traverse);
        };
        if (historyTree?.root) {
            traverse(historyTree.root);
        }
        return map;
    }, [historyTree?.root]);

    const currentNode = useMemo(() => {
        if (currentNodeId && nodesById.has(currentNodeId)) {
            return nodesById.get(currentNodeId);
        }
        return historyTree?.root ?? null;
    }, [currentNodeId, historyTree, nodesById]);

    const pathNodes = useMemo(() => {
        if (!currentNode) return [];
        const visited = new Set();
        const path = [];
        let node = currentNode;
        while (node && !visited.has(node.id)) {
            path.unshift(node);
            visited.add(node.id);
            node = node.parentId ? nodesById.get(node.parentId) : null;
        }
        return path;
    }, [currentNode, nodesById]);

    const hoveredPrevNodeId = useMemo(() => {
        if (!hoveredNodeId) return null;
        const idx = pathNodes.findIndex(n => n.id === hoveredNodeId);
        if (idx > 0) {
            return pathNodes[idx - 1]?.id ?? null;
        }
        return null;
    }, [hoveredNodeId, pathNodes]);

    const selectedPrevNodeId = useMemo(() => {
        if (!currentNodeId) return null;
        const idx = pathNodes.findIndex(n => n.id === currentNodeId);
        if (idx > 0) {
            return pathNodes[idx - 1]?.id ?? null;
        }
        return null;
    }, [currentNodeId, pathNodes]);

    useEffect(() => {
        if (typeof onScrollMetricsChange !== 'function') {
            return undefined;
        }
        const container = treeContainerRef.current;
        if (!container) {
            return undefined;
        }

        const handleScroll = () => reportTreeScrollMetrics();
        const handleResize = () => reportTreeScrollMetrics();

        container.addEventListener('scroll', handleScroll, { passive: true });

        let resizeObserver;
        if (typeof ResizeObserver === 'function') {
            resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(container);
        } else {
            window.addEventListener('resize', handleResize);
        }

        reportTreeScrollMetrics();

        return () => {
            container.removeEventListener('scroll', handleScroll);
            if (resizeObserver) {
                resizeObserver.disconnect();
            } else {
                window.removeEventListener('resize', handleResize);
            }
        };
    }, [onScrollMetricsChange, reportTreeScrollMetrics]);

    useLayoutEffect(() => {
        reportTreeScrollMetrics();
    }, [pathNodes, reportTreeScrollMetrics]);

    const pathUpToRoot = (startId, nodesById) => {
        const path = [];
        const visited = new Set();
        let node = startId ? nodesById.get(startId) : null;
        while (node && !visited.has(node.id)) {
            path.push(node.id);
            visited.add(node.id);
            node = node.parentId ? nodesById.get(node.parentId) : null;
        }
        return path;
    };

    const computeLcaAndPaths = (currentId, liveId, nodesById) => {
        if (!currentId || !liveId || !nodesById.has(currentId) || !nodesById.has(liveId)) {
            return { lcaId: null, upIds: [], downIds: [] };
        }

        const curUp = pathUpToRoot(currentId, nodesById);   // current -> ... -> root
        const liveUp = pathUpToRoot(liveId, nodesById);     // live -> ... -> root
        const liveSet = new Set(liveUp);

        // Find first node on current's path that is also on live's path
        const lcaId = curUp.find(id => liveSet.has(id)) ?? null;
        if (!lcaId) {
            return { lcaId: null, upIds: [], downIds: [] };
        }

        // upIds: from current back to (but not including) lca, in order current -> ... -> parentOfLca
        const upIds = [];
        for (const id of curUp) {
            if (id === lcaId) break;
            upIds.push(id);
        }

        // downIds: from first child after lca down to live, in order
        // liveUp is live -> ... -> lca -> ...; we want the segment lca->...->live in forward order.
        const idxLcaInLiveUp = liveUp.indexOf(lcaId);
        const segment = liveUp.slice(0, idxLcaInLiveUp); // live down to child-after-lca
        const downIds = segment.reverse(); // child-after-lca -> ... -> live

        return { lcaId, upIds, downIds };
    };

    const pathChildMap = useMemo(() => {
        const map = new Map();
        for (let i = 0; i < pathNodes.length - 1; i += 1) {
            map.set(pathNodes[i].id, pathNodes[i + 1].id);
        }
        return map;
    }, [pathNodes]);

    // Map from each ancestor of the live node to its child that leads toward live
    const livePathChildMap = useMemo(() => {
        const map = new Map();
        const liveId = historyTree?.liveNodeId ?? null;
        if (!liveId || !nodesById.has(liveId)) return map;

        // Build path from live node to root
        let node = nodesById.get(liveId);
        while (node?.parentId && nodesById.has(node.parentId)) {
            const parent = nodesById.get(node.parentId);
            map.set(parent.id, node.id);
            node = parent;
        }
        return map;
    }, [historyTree?.liveNodeId, nodesById]);

    const currentBranchChildren = useMemo(() => {
        if (!currentNode) return [];
        const nextOnPath = pathChildMap.get(currentNode.id);
        return (currentNode.children ?? []).filter(child => child.id !== nextOnPath);
    }, [currentNode, pathChildMap]);

    const lcaReplayPlan = useMemo(() => {
        const liveId = historyTree?.liveNodeId ?? null;
        const curId = currentNode?.id ?? null;
        if (!curId || !liveId) return { lcaId: null, upIds: [], downIds: [], queueIds: [] };

        const { lcaId, upIds, downIds } = computeLcaAndPaths(curId, liveId, nodesById);

        // queueIds: first go up (back in time) one parent at a time (excluding LCA),
        // then go forward (down) to live. For "up", we need to select each parent in order.
        // upIds currently lists nodes to leave; to move "up", we should select their parent each step.
        // We'll convert upIds into parent selections:
        const upSelectIds = [];
        let walker = nodesById.get(curId);
        for (const _ of upIds) {
            const parentId = walker?.parentId ?? null;
            if (parentId && nodesById.has(parentId)) {
            upSelectIds.push(parentId);
            walker = nodesById.get(parentId);
            }
        }

        // downIds are already in forward order child-after-lca -> ... -> live (select these directly)
        const queueIds = [...upSelectIds, ...downIds];

        return { lcaId, upIds, downIds, queueIds };
    }, [currentNode, historyTree?.liveNodeId, nodesById]);

    const pathToLive = lcaReplayPlan.queueIds;

    const nextLiveNode = (lcaReplayPlan.queueIds.length > 0)
        ? nodesById.get(lcaReplayPlan.queueIds[0]) ?? null
        : null;


    const clearReplayTimeout = useCallback(() => {
        const controller = replayStateRef.current;
        if (controller.timeoutId != null) {
            window.clearTimeout(controller.timeoutId);
            controller.timeoutId = null;
        }
    }, []);

    const stopReplay = useCallback(() => {
        clearReplayTimeout();
        const controller = replayStateRef.current;
        controller.active = false;
        controller.queue = [];
        setIsReplaying(false);
        onReplayStateChange?.(false);
    }, [clearReplayTimeout, onReplayStateChange]);

    // Helper to scroll a node element into view
    const scrollNodeIntoView = useCallback((nodeId) => {
        const container = treeContainerRef.current;
        if (!container || !nodeId) return;

        // Use setTimeout to let React update the DOM first
        setTimeout(() => {
            requestAnimationFrame(() => {
                const element = nodeElementRefs.current.get(nodeId);
                if (!element) return;

                const containerRect = container.getBoundingClientRect();
                const elementRect = element.getBoundingClientRect();
                const padding = 20;

                if (elementRect.top < containerRect.top + padding) {
                    const offset = elementRect.top - (containerRect.top + padding);
                    container.scrollBy({ top: offset, behavior: 'smooth' });
                } else if (elementRect.bottom > containerRect.bottom - padding) {
                    const offset = elementRect.bottom - (containerRect.bottom - padding);
                    container.scrollBy({ top: offset, behavior: 'smooth' });
                }
            });
        }, 50);
    }, []);

    // Helper to scroll to turn indicator (bottom of tree)
    const scrollToTurnIndicator = useCallback(() => {
        const container = treeContainerRef.current;
        if (!container) return;

        setTimeout(() => {
            requestAnimationFrame(() => {
                const indicator = turnIndicatorRef.current;
                if (indicator) {
                    const containerRect = container.getBoundingClientRect();
                    const indicatorRect = indicator.getBoundingClientRect();
                    const padding = 20;

                    if (indicatorRect.bottom > containerRect.bottom - padding) {
                        const offset = indicatorRect.bottom - (containerRect.bottom - padding);
                        container.scrollBy({ top: offset, behavior: 'smooth' });
                    }
                } else {
                    // Fallback: scroll to bottom
                    const maxScroll = container.scrollHeight - container.clientHeight;
                    if (maxScroll > 0) {
                        container.scrollTo({ top: maxScroll, behavior: 'smooth' });
                    }
                }
            });
        }, 50);
    }, []);

    const selectNode = useCallback((nodeId, options = {}) => {
        const { viaReplay = false, scrollIntoView = false } = options;
        if (openBranchFor !== null) {
            skipPreviewClearRef.current = true;
        }
        setOpenBranchFor(null);
        setFocusedBranchIndex(0);
        if (!viaReplay) {
            stopReplay();
        }
        onRequestFocus?.('history');
        onSelectNode?.(nodeId, { highlight: viaReplay ? true : false, viaReplay });

        // Scroll to the node if requested (for keyboard navigation)
        if (scrollIntoView) {
            scrollNodeIntoView(nodeId);
        }
    }, [openBranchFor, currentNodeId, onSelectNode, onRequestFocus, stopReplay, scrollNodeIntoView]);

    // Keep ref in sync so replay always calls the latest selectNode
    selectNodeRef.current = selectNode;

    const getReplayDelay = useCallback(() => {
        const speeds = [900, 450, 225]; // 1/2x, 1x, 2x
        return speeds[replaySpeedRef.current] ?? 450;
    }, []);

    const startReplayToLive = useCallback(() => {
        const ids = lcaReplayPlan.queueIds;
        if (!ids.length) return;

        stopReplay();
        const controller = replayStateRef.current;
        controller.active = true;
        controller.queue = [...ids];
        setIsReplaying(true);
        onReplayStateChange?.(true);

        const runStep = () => {
            const current = replayStateRef.current;
            if (!current.active) {
                clearReplayTimeout();
                return;
            }
            const nextId = current.queue.shift();
            if (nextId == null) {
                stopReplay();
                return;
            }
            // Use ref to always call the latest selectNode (avoids stale closure during replay)
            selectNodeRef.current(nextId, { viaReplay: true });
            current.timeoutId = window.setTimeout(runStep, getReplayDelay());
        };

        runStep();
    }, [lcaReplayPlan.queueIds, stopReplay, clearReplayTimeout, onReplayStateChange, getReplayDelay]);


    const branchPreviewRef = useRef(normalizePreviewEdge(null));
    const nodePreviewRef = useRef(normalizePreviewEdge(null));
    const appliedPreviewRef = useRef(normalizePreviewEdge(null));

    const applyPreviewEdge = useCallback((edge) => {
        if (!onPreviewTreeEdge) return;
        const normalized = normalizePreviewEdge(edge);
        if (previewEdgesEqual(appliedPreviewRef.current, normalized)) {
            return;
        }
        appliedPreviewRef.current = normalized;
        if (normalized.parentId && normalized.childId) {
            onPreviewTreeEdge({ from: { id: normalized.parentId }, to: { id: normalized.childId } });
        } else {
            onPreviewTreeEdge(null);
        }
    }, [onPreviewTreeEdge]);

    const updatePreviewEdge = useCallback(() => {
        const branchEdge = branchPreviewRef.current;
        if (branchEdge.parentId && branchEdge.childId) {
            applyPreviewEdge(branchEdge);
            return;
        }
        const nodeEdge = nodePreviewRef.current;
        if (nodeEdge.parentId && nodeEdge.childId) {
            applyPreviewEdge(nodeEdge);
            return;
        }
        applyPreviewEdge(null);
    }, [applyPreviewEdge]);

    const setBranchPreview = useCallback((edge) => {
        const normalized = normalizePreviewEdge(edge);
        if (previewEdgesEqual(branchPreviewRef.current, normalized)) {
            return;
        }
        branchPreviewRef.current = normalized;
        updatePreviewEdge();
    }, [updatePreviewEdge]);

    const setNodePreview = useCallback((edge) => {
        const normalized = normalizePreviewEdge(edge);
        if (previewEdgesEqual(nodePreviewRef.current, normalized)) {
            return;
        }
        nodePreviewRef.current = normalized;
        updatePreviewEdge();
    }, [updatePreviewEdge]);

    const handleNodeHover = useCallback((node, active) => {
        if (active) {
            if (node?.id) {
                setHoveredNodeId(node.id);
            }
        } else if (node?.id) {
            setHoveredNodeId(prev => (prev === node.id ? null : prev));
        } else if (!active) {
            setHoveredNodeId(null);
        }
        if (!node) {
            if (!active) {
                setNodePreview(null);
            }
            return;
        }
        if (active) {
            if (!node.parentId) {
                setNodePreview(null);
                return;
            }
            setNodePreview({ parentId: node.parentId, childId: node.id });
            return;
        }
        if (nodePreviewRef.current.childId !== node.id) {
            return;
        }
        setNodePreview(null);
    }, [setNodePreview, setHoveredNodeId]);

    useEffect(() => {
        if (openBranchFor && !nodesById.has(openBranchFor)) {
            setOpenBranchFor(null);
        }
    }, [nodesById, openBranchFor]);

    useEffect(() => {
        if (openBranchFor == null) {
            branchMenuRef.current = null;
        }
    }, [openBranchFor]);

    useEffect(() => {
        if (!openBranchFor) return;
        const menu = branchMenuRef.current;
        if (menu && typeof menu.focus === 'function') {
            menu.focus({ preventScroll: true });
        }
    }, [openBranchFor]);

    useEffect(() => {
        if (!openBranchFor) return;
        const menu = branchMenuRef.current;
        if (!menu) return;
        const options = menu.querySelectorAll('.tree-branch-option');
        const index = Math.max(0, Math.min(focusedBranchIndex, options.length - 1));
        const target = options[index];
        if (target && typeof target.scrollIntoView === 'function') {
            target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
    }, [focusedBranchIndex, openBranchFor]);

    // Close branch menu when clicking outside
    useEffect(() => {
        if (!openBranchFor) return;
        const handleClickOutside = (e) => {
            const menu = branchMenuRef.current;
            if (menu && !menu.contains(e.target)) {
                setOpenBranchFor(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openBranchFor]);

    useEffect(() => () => {
        stopReplay();
    }, [stopReplay]);

    // Track when we need to scroll after branch menu closes
    const pendingScrollRef = useRef(false);
    const branchWasRecentlyOpenRef = useRef(false);
    
    // Track when branch menu opens/closes
    useEffect(() => {
        if (openBranchFor !== null) {
            branchWasRecentlyOpenRef.current = true;
        }
    }, [openBranchFor]);
    
    // When path grows, check if branch was recently open
    useEffect(() => {
        const pathLength = pathNodes.length;
        const pathGrew = pathLength > prevPathLengthRef.current;
        const selectedNodeChanged = prevSelectedNodeRef.current !== currentNodeId;
        
        prevPathLengthRef.current = pathLength;
        prevSelectedNodeRef.current = currentNodeId;
        
        // If path grew and branch was recently open (even if now closed), scroll to bottom
        if ((pathGrew || selectedNodeChanged) && branchWasRecentlyOpenRef.current) {
            branchWasRecentlyOpenRef.current = false;
            
            const container = treeContainerRef.current;
            if (container) {
                // Wait for DOM to fully update
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        const maxScroll = container.scrollHeight - container.clientHeight;
                        if (maxScroll > 0) {
                            container.scrollTo({ top: maxScroll, behavior: 'smooth' });
                        }
                    });
                });
            }
        }
    }, [pathNodes.length, currentNodeId, openBranchFor]);

    // Clear the branchWasRecentlyOpen flag after a short delay if no path change happened
    useEffect(() => {
        if (openBranchFor === null && branchWasRecentlyOpenRef.current) {
            const timeoutId = setTimeout(() => {
                branchWasRecentlyOpenRef.current = false;
            }, 500);
            return () => clearTimeout(timeoutId);
        }
    }, [openBranchFor]);

    // Regular scroll effect for node changes (when no branch menu involved)
    useEffect(() => {
        if (isPreviewing || previewRecentlyActive()) return;
        if (openBranchFor !== null) return; // Branch menu is open
        if (branchWasRecentlyOpenRef.current) return; // Let the branch tracking handle it
        
        const container = treeContainerRef.current;
        if (!container || !currentNodeId) return;

        const liveNodeId = historyTree?.liveNodeId ?? null;
        const isOnLiveNode = liveNodeId != null && currentNodeId === liveNodeId;

        // Determine if we should scroll to bottom
        const shouldScrollToBottom = isOnLiveNode || firstRenderRef.current;

        const behavior = firstRenderRef.current ? 'auto' : 'smooth';
        firstRenderRef.current = false;

        if (shouldScrollToBottom) {
            // Scroll to bottom
            requestAnimationFrame(() => {
                const maxScroll = container.scrollHeight - container.clientHeight;
                if (maxScroll > 0) {
                    container.scrollTo({ top: maxScroll, behavior });
                }
            });
        } else {
            // Just scroll element into view
            requestAnimationFrame(() => {
                const element = nodeElementRefs.current.get(currentNodeId);
                if (!element) return;

                const containerRect = container.getBoundingClientRect();
                const elementRect = element.getBoundingClientRect();
                const padding = 20;

                if (elementRect.top < containerRect.top + padding) {
                    const offset = elementRect.top - (containerRect.top + padding);
                    container.scrollBy({ top: offset, behavior: 'smooth' });
                } else if (elementRect.bottom > containerRect.bottom - padding) {
                    const offset = elementRect.bottom - (containerRect.bottom - padding);
                    container.scrollBy({ top: offset, behavior: 'smooth' });
                }
            });
        }
    }, [pathNodes, currentNodeId, historyTree?.liveNodeId, openBranchFor, isPreviewing]);

    // Scroll branch menu into view when opened
    useEffect(() => {
        if (!openBranchFor) return;

        const timeoutId = setTimeout(() => {
            const menu = branchMenuRef.current;
            const container = treeContainerRef.current;
            if (!menu || !container) return;

            const menuRect = menu.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const padding = 32; // Extra padding to account for top: 48px

            // Scroll if menu extends beyond bottom
            const bottomOverflow = menuRect.bottom - containerRect.bottom + padding;
            if (bottomOverflow > 0) {
                container.scrollBy({ top: bottomOverflow, behavior: 'smooth' });
            }

            // Scroll if menu extends beyond top
            const topOverflow = containerRect.top - menuRect.top + padding;
            if (topOverflow > 0) {
                container.scrollBy({ top: -topOverflow, behavior: 'smooth' });
            }
        }, 50);

        return () => clearTimeout(timeoutId);
    }, [openBranchFor]);


    useEffect(() => {
        if (openBranchFor === currentNode?.id) {
            setFocusedBranchIndex(0);
        } else if (openBranchFor == null) {
            setFocusedBranchIndex(0);
        }
    }, [openBranchFor, currentNode?.id]);

    useEffect(() => {
        if (openBranchFor !== currentNode?.id) return;
        if (!currentBranchChildren.length) return;
        setFocusedBranchIndex(prev => {
            const capped = Math.min(Math.max(prev, 0), currentBranchChildren.length - 1);
            return capped;
        });
    }, [openBranchFor, currentNode?.id, currentBranchChildren.length]);

    useEffect(() => {
        if (skipPreviewClearRef.current) {
            skipPreviewClearRef.current = false;
            return;
        }
        const parentId = currentNode?.id ?? null;
        const idx = Math.min(Math.max(focusedBranchIndex, 0), currentBranchChildren.length - 1);
        const child = (openBranchFor === parentId && currentBranchChildren[idx]) ? currentBranchChildren[idx] : null;
        if (parentId && child) {
            setBranchPreview({ parentId, childId: child.id });
        } else {
            setBranchPreview(null);
        }
    }, [openBranchFor, currentNode?.id, currentBranchChildren, focusedBranchIndex, setBranchPreview]);

    useEffect(() => () => {
        branchPreviewRef.current = normalizePreviewEdge(null);
        nodePreviewRef.current = normalizePreviewEdge(null);
        appliedPreviewRef.current = normalizePreviewEdge(null);
        applyPreviewEdge(null);
    }, [applyPreviewEdge]);

    useEffect(() => {
        if (keyFocus !== 'history') {
            return;
        }
        const handleKey = (event) => {
            const target = event.target;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
                return;
            }


            // Handle Shift+S for speed control
            if (event.shiftKey && (event.key === 's' || event.key === 'S')) {
                event.preventDefault();
                setReplaySpeed(prev => {
                    const next = (prev + 1) % 3; // Cycle through 0, 1, 2
                    replaySpeedRef.current = next; // Update ref immediately for active replays
                    return next;
                });
                setSpeedFlash(true);
                setTimeout(() => setSpeedFlash(false), 200);
                return;
            }

            if (isReplaying) {
                if (event.key === 'r' || event.key === 'R') {
                    event.preventDefault();
                    stopReplay();
                }
                return;
            }
            const isMenuOpen = openBranchFor === currentNode?.id;
            const hasBranchChildren = currentBranchChildren.length > 0;

            if (event.key === 'Enter') {
                const handled = (() => {
                    if (hasBranchChildren && currentNode) {
                        event.preventDefault();
                        if (isMenuOpen) {
                            const idx = Math.min(Math.max(focusedBranchIndex, 0), currentBranchChildren.length - 1);
                            const chosen = currentBranchChildren[idx] ?? currentBranchChildren[0];
                            if (chosen) selectNode(chosen.id, { scrollIntoView: true });
                        } else {
                            setOpenBranchFor(currentNode.id);
                            setFocusedBranchIndex(0);
                        }
                        return true;
                    }
                    if (nextLiveNode) {
                        event.preventDefault();
                        selectNode(nextLiveNode.id, { scrollIntoView: true });
                        return true;
                    }
                    return false;
                })();
                if (handled) return;
            }

            if ((event.key === 'r' || event.key === 'R') && !event.repeat) {
                if (pathToLive.length) {
                    event.preventDefault();
                    startReplayToLive();
                }
                return;
            }

            // Navigation within branch menu
            if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && isMenuOpen && hasBranchChildren) {
                event.preventDefault();
                setFocusedBranchIndex(prev => {
                    const maxIdx = currentBranchChildren.length - 1;
                    if (maxIdx < 0) return 0;
                    if (event.key === 'ArrowDown') {
                        return Math.min(prev + 1, maxIdx);
                    }
                    return Math.max(prev - 1, 0);
                });
                return;
            }

            // Navigation up/down the tree path
            const lower = event.key.toLowerCase();
            const liveId = historyTree?.liveNodeId;
            const isAtLiveNode = currentNode?.id === liveId;

            if ((event.key === 'ArrowUp' || lower === 'w') && !isMenuOpen) {
                event.preventDefault();
                // If in turn mode, exit it first (go back to viewing HALT move)
                if (isInTurnMode && onExitTurnMode) {
                    onExitTurnMode();
                    // Scroll the live node into view after exiting turn mode
                    scrollNodeIntoView(liveId);
                    return;
                }
                // Navigate to parent
                if (currentNode?.parentId && nodesById.has(currentNode.parentId)) {
                    selectNode(currentNode.parentId, { scrollIntoView: true });
                }
                return;
            }

            if ((event.key === 'ArrowDown' || lower === 's') && !isMenuOpen) {
                event.preventDefault();
                const nextOnLivePath = livePathChildMap.get(currentNode?.id);

                // Priority 1: If at live node and not in turn mode, enter turn mode
                // This takes precedence over navigating to children (branches)
                if (isAtLiveNode && !isInTurnMode && onGoLive) {
                    const liveNode = nodesById.get(liveId);
                    const nextPlayer = liveNode?.statePlayer ?? 1;
                    onGoLive(nextPlayer);
                    // Scroll to make turn indicator visible
                    scrollToTurnIndicator();
                } else if (nextOnLivePath && nodesById.has(nextOnLivePath)) {
                    // Priority 2: Navigate to child on path to live
                    selectNode(nextOnLivePath, { scrollIntoView: true });
                } else if (currentNode?.children?.length > 0) {
                    selectNode(currentNode.children[0].id, { scrollIntoView: true });
                } else if (liveId && currentNode?.id !== liveId && nodesById.has(liveId)) {
                    selectNode(liveId, { scrollIntoView: true });
                }
                return;
            }
        };

        window.addEventListener('keydown', handleKey);
        return () => {
            window.removeEventListener('keydown', handleKey);
        };
    }, [currentBranchChildren, currentNode, focusedBranchIndex, historyTree, isReplaying, keyFocus, livePathChildMap, nextLiveNode, nodesById, openBranchFor, pathToLive, selectNode, setOpenBranchFor, setFocusedBranchIndex, startReplayToLive, stopReplay, isInTurnMode, onExitTurnMode, onGoLive, scrollToTurnIndicator, scrollNodeIntoView]);

    useEffect(() => {
        if (isReplaying) {
            setOpenBranchFor(null);
        }
    }, [isReplaying]);

    if (!pathNodes.length) {
        return (
            <div className="tree-view-container" ref={treeContainerRef}>
                <div className="tree-path-empty">History will appear here once moves are made.</div>
            </div>
        );
    }

    return (
        <div className="tree-view-container" ref={treeContainerRef}>
            <div className="tree-path-wrapper">
                {pathToLive.length > 0 && (
                    <div className="tree-replay-controls">
                        <button
                            type="button"
                            className="tree-replay-button tree-branch-button"
                            disabled={isReplaying}
                            onClick={(e) => {
                                onRequestBranch?.();
                                e.currentTarget.blur();
                            }}
                        >
                            Branch here
                            <span className="tree-replay-hint">B</span>
                        </button>
                        <button
                            type="button"
                            className={`tree-replay-button ${isReplaying ? 'active' : ''}`}
                            onClick={(e) => {
                                if (isReplaying) {
                                    stopReplay();
                                } else {
                                    startReplayToLive();
                                }
                                e.currentTarget.blur();
                            }}
                        >
                            {isReplaying ? 'Stop replay' : 'Replay to live'}
                            <span className="tree-replay-hint">R</span>
                        </button>
                        <button
                            type="button"
                            className={`replay-speed-indicator ${speedFlash ? 'flash' : ''}`}
                            onClick={(e) => {
                                setReplaySpeed(prev => {
                                    const next = (prev + 1) % 3;
                                    replaySpeedRef.current = next;
                                    setSpeedFlash(true);
                                    setTimeout(() => setSpeedFlash(false), 200);
                                    return next;
                                });
                                e.currentTarget.blur();
                            }}
                        >
                            {[0, 1, 2].map((index) => (
                                <div
                                    key={index}
                                    className={`speed-rect ${index === replaySpeed ? 'active' : ''}`}
                                />
                            ))}
                        </button>
                    </div>
                )}
                <ul className="tree-path-list">
                    {pathNodes.map((node, index) => {
                        const nextOnPath = pathChildMap.get(node.id);
                        const branchChildren = (node.children ?? []).filter(child => child.id !== nextOnPath);
                        const hasBranches = branchChildren.length > 0;
                        const isLive = node.id === historyTree?.liveNodeId;
                        // Node is visually "current" unless we're at live AND in turn mode
                        // (in which case the turn indicator is the current selection)
                        const isCurrent = node.id === currentNodeId && !(isLive && isInTurnMode);
                        const rowClasses = ['tree-node-row'];
                        if (isCurrent) rowClasses.push('current');
                        const itemClasses = ['tree-path-item'];
                        const isBranchMenuOpen = openBranchFor === node.id;
                        if (isBranchMenuOpen) {
                            itemClasses.push('branch-menu-open');
                        }
                        if (isCurrent) {
                            itemClasses.push('current');
                        } else if (isLive) {
                            itemClasses.push('live');
                        }
                        const isBranchReady = branchReadyNodeId != null && branchReadyNodeId === node.id;
                        const shouldFlashNode = flashActive && highlightNodeId === node.id;
                        if (shouldFlashNode) {
                            itemClasses.push('highlight-flash');
                        }
                        const display = formatDisplay(node);
                        const statePlayer = node.statePlayer ? `P${node.statePlayer}` : '•';
                        const previewingAsSource = previewedTreeEdge?.parentId === node.id;
                        const previewingAsTarget = previewedTreeEdge?.childId === node.id;
                        const bubblePrevHighlight = (
                            (hoveredPrevNodeId && hoveredPrevNodeId === node.id)
                            || (selectedPrevNodeId && selectedPrevNodeId === node.id)
                        );
                        if (previewingAsTarget) {
                            itemClasses.push('preview-target');
                        }
                        const summaryClasses = ['tree-node-summary'];
                        if (shouldFlashNode) {
                            summaryClasses.push('highlight-flash');
                        }
                        if (previewingAsTarget) {
                            summaryClasses.push('preview-active');
                        }

                        return (
                            <li
                                key={node.id}
                                className={itemClasses.join(' ')}
                                ref={(el) => {
                                    if (el) {
                                        nodeElementRefs.current.set(node.id, el);
                                    } else {
                                        nodeElementRefs.current.delete(node.id);
                                    }
                                }}
                            >
                                <div
                                    className={rowClasses.join(' ')}
                                    onMouseEnter={() => handleNodeHover(node, true)}
                                    onMouseLeave={() => handleNodeHover(node, false)}
                                >
                                    <div className="tree-branch-area left" />

                                        <div className={`tree-node-marker ${index === 0 ? 'is-root' : ''}`}>
                                            <div className={`tree-node-line top ${index === 0 ? 'hidden' : ''}`} />
                                            <div className={`tree-node-dot ${isCurrent ? 'current' : ''}`} />
                                            <div className={`tree-node-line bottom ${index === pathNodes.length - 1 ? 'hidden' : ''}`} />
                                        </div>

                                        <div className="tree-node-details">
                                        <button
                                            type="button"
                                            className={summaryClasses.join(' ')}
                                            onClick={(e) => { selectNode(node.id); e.currentTarget.blur(); }}
                                        >
                                            {display.isStart ? (
                                                <div className="tree-node-summary-main">
                                                    <div className="tree-node-primary-line">
                                                        <span className="tree-node-title">Game Start</span>
                                                        {isLive && <span className="tree-node-tag live">Live</span>}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="tree-node-summary-main">
                                                    <div className="tree-node-primary-line">
                                                        <span className="tree-node-player-label">{display.playerLabel}</span>
                                                        {display.actionLabel && (
                                                            <span className={`tree-node-action action-${display.actionLabel.toLowerCase()}`}>
                                                                {display.actionLabel}
                                                            </span>
                                                        )}
                                                        {isLive && <span className="tree-node-tag live">Live</span>}
                                                    </div>
                                                    {display.singleCoord && (
                                                        <span className="tree-node-coords">{display.singleCoord}</span>
                                                    )}
                                                    {!display.singleCoord && display.startCoord && display.endCoord && (
                                                        <span className="tree-node-coords">
                                                            <span>{display.startCoord}</span>
                                                            <span className="tree-node-arrow">→</span>
                                                            <span>{display.endCoord}</span>
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </button>
                                        <div className="tree-node-meta">
                                            {node.turn != null && (
                                                <span className="tree-node-turn">Turn {node.turn}</span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="tree-node-status">
                                        {hasBranches && (
                                            <>
                                                <button
                                                    type="button"
                                                    className={`tree-branch-toggle ${openBranchFor === node.id ? 'open' : ''}`}
                                                    disabled={isReplaying}
                                                    onClick={(e) => {
                                                        stopReplay();
                                                        setOpenBranchFor(prev => (prev === node.id ? null : node.id));
                                                        e.currentTarget.blur();
                                                    }}
                                                    aria-label="Show alternate continuations"
                                                >
                                                    +
                                                </button>
                                                {openBranchFor === node.id && (
                                                    <div
                                                        className="tree-branch-menu tree-branch-menu--left"
                                                        ref={(el) => {
                                                            if (openBranchFor === node.id) {
                                                                branchMenuRef.current = el;
                                                            }
                                                        }}
                                                        tabIndex={-1}
                                                        role="menu"
                                                        onWheel={(event) => {
                                                            event.stopPropagation();
                                                        }}
                                                        onKeyDown={(event) => {
                                                            const key = event.key;
                                                            const lower = key.toLowerCase();
                                                            if (key === 'ArrowUp' || lower === 'w') {
                                                                event.stopPropagation();
                                                                setFocusedBranchIndex(prev => Math.max(prev - 1, 0));
                                                                event.preventDefault();
                                                            } else if (key === 'ArrowDown' || lower === 's') {
                                                                event.stopPropagation();
                                                                setFocusedBranchIndex(prev => Math.min(prev + 1, branchChildren.length - 1));
                                                                event.preventDefault();
                                                            } else if (key === 'Enter') {
                                                                event.stopPropagation();
                                                                event.preventDefault();
                                                                const chosen = branchChildren[Math.max(0, Math.min(focusedBranchIndex, branchChildren.length - 1))];
                                                                if (chosen) selectNode(chosen.id, { scrollIntoView: true });
                                                            } else if (key === 'Escape') {
                                                                event.stopPropagation();
                                                                event.preventDefault();
                                                                setOpenBranchFor(null);
                                                                setFocusedBranchIndex(0);
                                                            }
                                                        }}
                                                    >
                                                    {branchChildren.map((child, childIdx) => {
                                                        const displayChild = formatDisplay(child);
                                                        const isFocused = focusedBranchIndex === childIdx;
                                                        const statePlayerLabel = child.statePlayer ? `P${child.statePlayer}` : '•';
                                                        return (
                                                            <button
                                                                key={child.id}
                                                                type="button"
                                                                className={`tree-branch-option ${isFocused ? 'focused' : ''}`}
                                                                    onClick={(e) => { selectNode(child.id, { scrollIntoView: true }); e.currentTarget.blur(); }}
                                                                    onMouseEnter={() => {
                                                                        setFocusedBranchIndex(childIdx);
                                                                    }}
                                                                    onFocus={() => setFocusedBranchIndex(childIdx)}
                                                                >
                                                                    <div className="tree-branch-option-text">
                                                                        <div className="tree-branch-option-main">
                                                                            <span className="tree-node-player-label">{displayChild.playerLabel}</span>
                                                                            {displayChild.actionLabel && (
                                                                                <span className={`tree-node-action action-${displayChild.actionLabel.toLowerCase()}`}>
                                                                                    {displayChild.actionLabel}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {displayChild.singleCoord && (
                                                                            <span className="tree-node-coords">{displayChild.singleCoord}</span>
                                                                        )}
                                                                        {!displayChild.singleCoord && displayChild.startCoord && displayChild.endCoord && (
                                                                            <span className="tree-node-coords">
                                                                                <span>{displayChild.startCoord}</span>
                                                                                <span className="tree-node-arrow">→</span>
                                                                                <span>{displayChild.endCoord}</span>
                                                                            </span>
                                                                        )}
                                                                        {child.turn != null && (
                                                                            <div className="tree-branch-option-meta">
                                                                                <span className="tree-node-turn">Turn {child.turn}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <span className={`tree-node-status-button tree-branch-option-status player-${child.statePlayer ?? 'neutral'}`}>
                                                                        {statePlayerLabel}
                                                                    </span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {nnVisualizationEnabled && onToggleVisualization && (() => {
                                            const status = nnDataStatus.get(node.id);
                                            const statusClass = status ? `status-${status.toLowerCase()}` : '';
                                            return (
                                                <button
                                                    type="button"
                                                    className={`tree-visualization-toggle ${visualizationNodeId === node.id ? 'active' : ''} ${statusClass}`}
                                                    disabled={isReplaying}
                                                    onClick={(e) => {
                                                        onToggleVisualization(node.id);
                                                        e.currentTarget.blur();
                                                    }}
                                                    aria-label="Cycle neural network visualization"
                                                    title={`NN visualization${status ? ` (${status})` : ''}`}
                                                >
                                                    {status === 'Processing' ? '⟳' : status === 'Failed' ? '✕' : '◈'}
                                                </button>
                                            );
                                        })()}
                                        <button
                                            type="button"
                                            className={[
                                                'tree-node-status-button',
                                                `player-${node.statePlayer ?? 'neutral'}`,
                                                isLive ? 'live' : '',
                                                (!isLive && isBranchReady) ? 'branch-ready' : '',
                                                previewingAsSource ? 'preview-active' : '',
                                                previewingAsSource ? 'preview-source' : '',
                                                bubblePrevHighlight ? 'prev-highlight' : '',
                                            ].filter(Boolean).join(' ')}
                                            onClick={(e) => {
                                                if (!isCurrent) {
                                                    selectNode(node.id, { scrollIntoView: true });
                                                } else {
                                                    onRequestBranch?.();
                                                }
                                                e.currentTarget.blur();
                                            }}
                                            title={node.moveDesc || display.playerLabel}
                                        >
                                            {statePlayer}
                                        </button>
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                    {/* "P#'s Turn" entry - only shown when at the live node */}
                    {(() => {
                        const liveId = historyTree?.liveNodeId;
                        if (!liveId) return null;

                        const liveNode = nodesById.get(liveId);
                        if (!liveNode) return null;

                        const isAtLive = currentNodeId === liveId;
                        // Only show when we're at the live node
                        if (!isAtLive) return null;

                        const nextPlayer = liveNode.statePlayer ?? 1;
                        // This entry is "selected" when in turn mode
                        const isTurnSelected = isInTurnMode;

                        const handleTurnClick = (e) => {
                            e.currentTarget.blur();
                            if (onGoLive) {
                                onGoLive(nextPlayer);
                            } else {
                                selectNode(liveId);
                            }
                        };

                        const turnLabel = `P${nextPlayer}'s Turn`;
                        const turnTitle = isTurnSelected
                            ? `Currently ready to play as P${nextPlayer}`
                            : `Switch to P${nextPlayer}'s perspective to play`;

                        return (
                            <li
                                key="turn-indicator"
                                ref={turnIndicatorRef}
                                className={`tree-path-item tree-turn-indicator ${isTurnSelected ? 'current' : ''}`}
                            >
                                <div className={`tree-node-row ${isTurnSelected ? 'turn-selected' : ''}`}>
                                    <div className="tree-branch-area left" />
                                    <div className="tree-node-marker is-leaf">
                                        <div className="tree-node-line top" />
                                        <div className={`tree-node-dot turn-indicator-dot ${isTurnSelected ? 'current' : ''}`} />
                                        <div className="tree-node-line bottom hidden" />
                                    </div>
                                    <div className="tree-node-details">
                                        <button
                                            type="button"
                                            className={`tree-node-summary turn-indicator-summary ${isTurnSelected ? 'selected' : ''}`}
                                            onClick={handleTurnClick}
                                            title={turnTitle}
                                        >
                                            <div className="tree-node-summary-main">
                                                <div className="tree-node-primary-line">
                                                    <span className="tree-node-title turn-title">{turnLabel}</span>
                                                </div>
                                            </div>
                                        </button>
                                    </div>
                                    <div className="tree-node-status">
                                        <button
                                            type="button"
                                            className={`tree-node-status-button player-${nextPlayer} ${isTurnSelected ? 'live' : ''}`}
                                            onClick={handleTurnClick}
                                            title={turnTitle}
                                        >
                                            P{nextPlayer}
                                        </button>
                                    </div>
                                </div>
                            </li>
                        );
                    })()}
                </ul>
            </div>
        </div>
    );
};